import { useState } from 'react'
import { ChurnReasons, TerminateContractRequest, TerminateStepProps, TerminateSteps } from '../../types'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import { DATE_FORMAT } from 'constants/constants'
import { fileToBase64 } from 'utils/files'
import { getAxiosRequestConfig, newRequest } from 'utils/request'
import { Register } from 'types/types'
import { Methods, Response } from 'types/request'
import { generateContractsDataWithIndexes } from 'utils/contracts'
import contractsStyles from '../../../Contracts.module.scss'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { log, setLogContext, truncateStringForLogging } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { isResponseCode } from 'utils/errors.ts'
import DataBlock from 'features/contracts/add/components/data-block/DataBlock.tsx'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'
import { Card, Heading, InlineMessage } from '@boltenergy-be/design-system'
import FormButtons from 'features/contracts/components/form-buttons/FormButtons.tsx'
import { setMoveFlow, setMoveFlowState } from 'store/app/slice.ts'
import { LanguageLowercase } from 'store/app/types.ts'
import { IS_BETA, IS_PROD_TESTING } from 'constants/envs.ts'
import { selectCurrentBillingContract } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { MOVE_FLOWS, TIMEFRAME_CODES } from '@boltenergy-be/shared'

const Overview = ({ setCurrentStep, terminateData }: TerminateStepProps) => {
  // REDUX STORE
  const { contact } = useStoreSelector((store) => store.contact)
  const { move } = useStoreSelector((store) => store.app)
  const billingContract = useStoreSelector(selectCurrentBillingContract)
  const { electricity, gas } = billingContract.serviceContracts
  const dispatch = useStoreDispatch()

  // Local state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  // i18n
  const { t } = useTranslation(['contracts', 'common', 'errors'])

  // Constants
  const isDateInFuture = dayjs(terminateData[TerminateSteps.METER_DETAILS].date).isAfter(dayjs())
  const cantTerminateContract =
    !terminateData[TerminateSteps.METER_DETAILS].digitalMeter &&
    !isDateInFuture &&
    typeof electricity.deliveryPoint.previousIndex === 'undefined'

  /**
   * Handles the form submit
   * Terminates the contract
   */
  const onSubmit = async () => {
    setIsLoading(true)

    if (IS_BETA || IS_PROD_TESTING) {
      log({ error: 'Cannot terminate a contract in beta', identifier: '[BETA:contracts]' })
      return
    }

    setLogContext('terminate_contract_raw_inputs', terminateData)

    try {
      const requestData: TerminateContractRequest = {
        // Supplier switch default (but should never be the case). Necessary due to undefined initial value
        churnReason: terminateData[TerminateSteps.CHURN] || ChurnReasons.SUPPLIER_SWITCH,
        date: terminateData[TerminateSteps.METER_DETAILS].date,
        deliveryAddress: electricity.deliveryPoint.address,
        digitalMeter: terminateData[TerminateSteps.METER_DETAILS].digitalMeter,
        ...(terminateData[TerminateSteps.METER_DETAILS].docUpload && {
          document: (await fileToBase64(terminateData[TerminateSteps.METER_DETAILS].docUpload[0])) as string
        }),
        email: contact?.email || '',
        language: contact?.language?.toLowerCase() as LanguageLowercase,
        referral: terminateData[TerminateSteps.REFERRAL],
        contracts: generateContractsDataWithIndexes(terminateData[TerminateSteps.METER_DETAILS].indexes, electricity, gas, isDateInFuture),
        moveFlow: move.flow
      }

      setLogContext(
        'terminate_contract_request_body',
        requestData?.document
          ? ({ ...requestData, document: truncateStringForLogging(requestData.document) } satisfies TerminateContractRequest)
          : requestData
      )

      const { success, message, errorCode }: Response = await newRequest(
        getAxiosRequestConfig(Methods.PATCH, `contracts/${billingContract.contractNumber}/terminate`, requestData)
      )

      if (success) {
        // Add the move flow state to the store. `move.stopped` is what makes the done page report the stop as
        // completed, so it may only be set once the backend accepted the termination.
        dispatch(
          setMoveFlowState({
            key: 'stopped',
            state: {
              address: electricity.deliveryPoint.address,
              date: terminateData[TerminateSteps.METER_DETAILS].date
            }
          })
        )

        // Change the move flow when the reason is "Move in Belgium" for extra CTA on last step
        if (move.flow !== MOVE_FLOWS.startStop && terminateData[TerminateSteps.CHURN] === ChurnReasons.MOVE_IN_BELGIUM) {
          dispatch(setMoveFlow(MOVE_FLOWS.stopStart))
        }

        trackEvent(
          { category: 'contract', object: 'terminate_contract', action: 'finish' },
          {
            flow: move.flow,
            type: ContractFlowTrackingTypes.TERMINATE,
            reason: terminateData[TerminateSteps.CHURN],
            referral: !!terminateData[TerminateSteps.REFERRAL]?.email
          }
        )
        setCurrentStep(TerminateSteps.DONE)
      } else {
        const responseCode = [errorCode, message].find(isResponseCode)
        log({
          error: message || 'Failed to terminate contract',
          metaData: {
            data: {
              message,
              requestData
            }
          },
          identifier: `[${Flow.CONTRACT}:terminateContract${responseCode ? `:${responseCode}` : ''}]`,
          errorCode: responseCode
        })
        setError(true)
      }
    } catch (error) {
      log({
        error: error as Error,
        identifier: `[${Flow.CONTRACT}:terminateContractCatch]`
      })
      setError(true)
    }

    setIsLoading(false)
  }

  return (
    <div className={contractsStyles['form-card']}>
      <div className={contractsStyles['form-content']}>
        <header>
          <Card.Title>{t(($) => $.terminate.overview.title)}</Card.Title>
          {!cantTerminateContract && <p className="mt-400">{t(($) => $.terminate.overview.description)}</p>}
        </header>

        {cantTerminateContract ? (
          <p>{parse(t(($) => $.terminate.overview.cantTerminate))}</p>
        ) : (
          <section className={contractsStyles['overview-section']}>
            {terminateData[TerminateSteps.CHURN] && (
              <DataBlock label={t(($) => $.terminate.overview.fields.churn)}>
                {t(($) => $.terminate.churn.reasons[terminateData[TerminateSteps.CHURN]!])}
              </DataBlock>
            )}

            <DataBlock label={t(($) => $.terminate.overview.fields.date)}>
              {dayjs(terminateData[TerminateSteps.METER_DETAILS].date).format(DATE_FORMAT)}
            </DataBlock>

            <DataBlock label={t(($) => $.terminate.overview.fields.document)}>
              {terminateData[TerminateSteps.METER_DETAILS].docUpload
                ? terminateData[TerminateSteps.METER_DETAILS].docUpload[0].name
                : t(($) => $.fields.document.none)}
            </DataBlock>

            {terminateData[TerminateSteps.METER_DETAILS].digitalMeter || isDateInFuture ? (
              <>
                <DataBlock label={t(($) => $.terminate.overview.fields.indexes)}>
                  {t(
                    ($) =>
                      $.fields.meterReadings[
                        terminateData[TerminateSteps.METER_DETAILS].digitalMeter ? 'messageDigitalMeter' : 'messageDateInFuture'
                      ]
                  )}
                </DataBlock>
              </>
            ) : (
              <>
                <div className={contractsStyles['overview-topics']}>
                  <Heading as="h3" variant="h6">
                    {t(($) => $.indexes.title, {
                      ns: 'common'
                    })}{' '}
                    {t(($) => $.electricity, {
                      ns: 'common'
                    }).toLowerCase()}
                  </Heading>

                  {electricity.deliveryPoint.previousIndex?.registers
                    .filter((indexRegister: Register) => indexRegister.timeframeCode !== TIMEFRAME_CODES.notUsed)
                    .map((_, index: number) => (
                      <DataBlock key={index}>
                        {(terminateData[TerminateSteps.METER_DETAILS].indexes.electricity?.[index] ?? 0).toString()}
                      </DataBlock>
                    ))}
                </div>

                {gas && (
                  <div className={contractsStyles['overview-topics']}>
                    <Heading as="h3" variant="h6">
                      {t(($) => $.indexes.title, {
                        ns: 'common'
                      })}{' '}
                      {t(($) => $.gas, {
                        ns: 'common'
                      }).toLowerCase()}
                    </Heading>

                    <DataBlock>{(terminateData[TerminateSteps.METER_DETAILS].indexes.gas?.[0] ?? 0).toString()}</DataBlock>
                  </div>
                )}
              </>
            )}

            {error && (
              <InlineMessage type="negative">
                {t(($) => $.default, {
                  ns: 'errors'
                })}
              </InlineMessage>
            )}
          </section>
        )}
      </div>

      <FormButtons
        submitDisabled={IS_PROD_TESTING || error || isLoading || cantTerminateContract}
        loading={isLoading}
        currentStep={TerminateSteps.OVERVIEW}
        trackingId={ContractFlowTrackingTypes.TERMINATE}
        onSubmit={onSubmit}
      />
    </div>
  )
}

export default Overview
